import React from "react";
import { cn } from "@/lib/utils/cn";
import { Slot } from "@radix-ui/react-slot";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "secondary" | "outline" | "ghost" | "destructive" | "link" | "premium";
  size?: "xs" | "sm" | "default" | "lg" | "xl" | "icon";
  isLoading?: boolean;
  asChild?: boolean;
}

const variants: Record<string, string> = {
  default:
    "bg-foreground text-background shadow-sm hover:bg-foreground/90 active:bg-foreground/95",
  secondary:
    "bg-neutral-100 text-foreground hover:bg-neutral-200 active:bg-neutral-300",
  outline:
    "border border-border bg-transparent hover:bg-neutral-50 hover:text-foreground active:bg-neutral-100 hover:border-neutral-300",
  ghost:
    "bg-transparent text-foreground/60 hover:text-foreground hover:bg-neutral-100 active:bg-neutral-200",
  destructive:
    "bg-destructive text-white shadow-sm hover:bg-destructive/90 active:bg-destructive/95",
  link:
    "text-primary underline underline-offset-4 decoration-primary/30 hover:decoration-primary/60",
  premium:
    "bg-gradient-to-r from-primary-500 to-primary-400 text-white shadow-sm hover:shadow-md hover:from-primary-600 hover:to-primary-500 active:from-primary-700 active:to-primary-600",
};

const sizes: Record<string, string> = {
  xs: "h-8 px-2.5 text-2 font-medium rounded-2",
  sm: "h-9 px-4 text-3 font-medium rounded-3",
  default: "h-10 px-5 text-3 font-medium rounded-3",
  lg: "h-11 px-6 text-4 font-medium rounded-4",
  xl: "h-12 px-8 text-5 font-medium rounded-4",
  icon: "h-10 w-10 rounded-3",
};

export function Button({
  className,
  variant = "default",
  size = "default",
  isLoading = false,
  disabled,
  children,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  let content: React.ReactNode;

  if (asChild) {
    if (isLoading && children && React.isValidElement(children)) {
      content = React.cloneElement(children, {
        children: (
          <>
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {children.props.children}
          </>
        ),
      });
    } else {
      content = children;
    }
  } else {
    content = (
      <>
        {isLoading && (
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
        {children}
      </>
    );
  }

  return (
    <Comp
      className={cn(
        "inline-flex items-center justify-center gap-2 font-medium transition-all duration-3 ease focus-visible:outline-none focus-visible:ring-sm focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-40 select-none active:scale-[0.97]",
        variants[variant],
        sizes[size],
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {content}
    </Comp>
  );
}

Button.displayName = "Button";
